import { useAllJobErrors } from '@commenda/api';
import { getIndirectTaxFilingV2BreakdownErrors } from '@commenda/api/indirect-tax/filings';
import {
  Badge,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  LoadingIndicator,
} from '@commenda/kakashi';
import { useEffect, useMemo, useState } from 'react';

import { IndirectTaxFilingsStatusRowSource } from '../types';

interface JobErrorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  /** Heading shown under the title (e.g. the filing name). */
  label?: string;
  source: IndirectTaxFilingsStatusRowSource | null;
}

/** Table-ready, source-agnostic error row. */
interface NormalizedJobError {
  id: string;
  identifier: string;
  errorMessage: string;
  rawRow: string;
  createdAt: number;
  /** Invoice context — only present for filing-sourced (US/CA breakdown) errors. */
  transactedAt?: string;
  reportingDateOverride?: string;
  sourcePlatform?: string;
  sourcePlatformId?: string;
}

const MAX_JOB_ERRORS = 100;

function formatTimestamp(createdAtMicros: number): string {
  if (!createdAtMicros) return '—';
  const date = new Date(createdAtMicros / 1000);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function JobErrorsTable({
  errors,
  isLoading,
  error,
  total,
}: {
  errors: NormalizedJobError[];
  isLoading: boolean;
  error?: string | null;
  total: number;
}) {
  const columnGroups = useMemo(
    () => [
      {
        id: 'job-errors',
        columns: [
          {
            accessorKey: 'identifier',
            header: 'Record',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="font-mono txt-small text-text-secondary">
                {row.original.identifier}
              </span>
            ),
          },
          {
            accessorKey: 'errorMessage',
            header: 'Error',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="txt-small-medium text-text-primary">
                {row.original.errorMessage}
              </span>
            ),
          },
          {
            accessorKey: 'transactedAt',
            header: 'Transaction date',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="txt-small text-text-secondary">
                {row.original.transactedAt || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'reportingDateOverride',
            header: 'Reporting date override',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="txt-small text-text-secondary">
                {row.original.reportingDateOverride || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'sourcePlatform',
            header: 'Source platform',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="txt-small text-text-secondary">
                {row.original.sourcePlatform || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'sourcePlatformId',
            header: 'Source platform ID',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="font-mono txt-small text-text-tertiary">
                {row.original.sourcePlatformId || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'rawRow',
            header: 'Raw row',
            enableSorting: false,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="font-mono txt-small text-text-tertiary">
                {row.original.rawRow || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'createdAt',
            header: 'When',
            enableSorting: true,
            cell: ({ row }: { row: { original: NormalizedJobError } }) => (
              <span className="txt-small text-text-secondary">
                {formatTimestamp(row.original.createdAt)}
              </span>
            ),
          },
        ],
      },
    ],
    []
  );

  if (isLoading && errors.length === 0) {
    return (
      <div className="flex items-center justify-center py-10xl">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) {
    return (
      <div className="txt-small-regular text-text-error py-xl">{error}</div>
    );
  }

  return (
    <div className="flex flex-col gap-md">
      <Badge variant="error">
        {total} error{total === 1 ? '' : 's'}
      </Badge>
      <DataTable
        columnGroups={columnGroups}
        data={errors}
        emptyStateConfig={{
          title: 'No errors found',
          description: 'There are no recorded errors for this selection.',
        }}
      />
    </div>
  );
}

/** EU/UK contribution: errors come straight off the job id. */
function JobErrorsByJobId({
  corporationId,
  jobId,
}: {
  corporationId: number;
  jobId: string;
}) {
  const { jobErrors, totalJobErrors, isLoading, error } = useAllJobErrors({
    job_id: jobId,
    corporation_id: corporationId,
    limit: MAX_JOB_ERRORS,
  });

  const normalized = useMemo<NormalizedJobError[]>(
    () =>
      jobErrors.map((e) => ({
        id: e.id,
        identifier: `Row ${e.row_number}`,
        errorMessage: e.error_message,
        rawRow: e.raw_row,
        createdAt: e.created_at,
      })),
    [jobErrors]
  );

  return (
    <JobErrorsTable
      errors={normalized}
      isLoading={isLoading}
      error={error ? 'Failed to load job errors.' : null}
      total={totalJobErrors}
    />
  );
}

/** US/CA breakdown: per-invoice errors are resolved from the filing endpoint. */
function JobErrorsByFiling({
  corporationId,
  filingId,
}: {
  corporationId: number;
  filingId: string;
}) {
  const [errors, setErrors] = useState<NormalizedJobError[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void getIndirectTaxFilingV2BreakdownErrors({
      params: { corporationId, filingId },
    }).then((result) => {
      if (cancelled) return;
      if (!result.success || !result.data) {
        setError(result.error ?? 'Failed to load tax breakdown errors.');
        setErrors([]);
      } else {
        setErrors(
          result.data.taxBreakdownErrors.map((e) => ({
            id: e.jobErrorId,
            identifier:
              e.invoiceDetails.sourcePlatformId || e.invoiceDetails.id,
            errorMessage: e.errorMessage,
            rawRow: '',
            createdAt: e.createdAt,
            transactedAt: e.invoiceDetails.transactedAt,
            reportingDateOverride: e.invoiceDetails.reportingDateOverride,
            sourcePlatform: e.invoiceDetails.sourcePlatform,
            sourcePlatformId: e.invoiceDetails.sourcePlatformId,
          }))
        );
      }
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [corporationId, filingId]);

  return (
    <JobErrorsTable
      errors={errors}
      isLoading={isLoading}
      error={error}
      total={errors.length}
    />
  );
}

/**
 * Admin-only inspector for the job errors behind a failed status row. Sourced
 * either from a job id (EU/UK contribution) or a filing id (US/CA breakdown).
 */
const JobErrorsModal = ({
  isOpen,
  onClose,
  corporationId,
  label,
  source,
}: JobErrorsModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-white flex max-h-[80vh] w-[95vw] min-w-[800px] max-w-[1000px] flex-col gap-0 overflow-hidden p-none"
        preventCloseOnOutsideClick
      >
        <DialogHeader className="shrink-0 border-b border-border-primary flex-col items-start">
          <DialogTitle>Job errors</DialogTitle>
          {label ? <DialogDescription>{label}</DialogDescription> : null}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-3xl">
          {source?.type === 'job' ? (
            <JobErrorsByJobId
              corporationId={corporationId}
              jobId={source.jobId}
            />
          ) : source?.type === 'filing' ? (
            <JobErrorsByFiling
              corporationId={corporationId}
              filingId={source.filingId}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { JobErrorsModal };
